import React, { useState, useEffect } from 'react';
import { fmtDate, fmtDateTime } from '@shared/formatDate';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  Badge as BadgeIcon,
  LocationOn as LocationIcon,
  Shield as ShieldIcon,
  Business as BusinessIcon,
  Add as AddIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import trustHubService, {
  TrustHubDashboard,
  TrustHubCredential,
  VerificationItem,
} from '../services/trustHubService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
  </div>
);

const TrustHub: React.FC = () => {
  const credentialingWizardUrl = 'https://register.qixit.com/register';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboard, setDashboard] = useState<TrustHubDashboard | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [triggeringVerification, setTriggeringVerification] = useState<string | null>(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await trustHubService.getDashboard();
      if (response.success) {
        setDashboard(response.dashboard);
      } else {
        setError('Failed to load Trust Hub data');
      }
    } catch (err) {
      console.error('Error loading Trust Hub:', err);
      setError('Failed to load Trust Hub');
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerVerification = async (verificationType: string) => {
    try {
      setTriggeringVerification(verificationType);
      const result = await trustHubService.triggerVerification(verificationType);
      if (result.success) {
        await loadDashboard();
      }
    } catch (err) {
      console.error('Error triggering verification:', err);
    } finally {
      setTriggeringVerification(null);
    }
  };

  const getVerificationIcon = (type: string) => {
    switch (type) {
      case 'EMAIL':
        return <EmailIcon />;
      case 'PHONE':
        return <PhoneIcon />;
      case 'ID':
        return <BadgeIcon />;
      case 'ADDRESS':
        return <LocationIcon />;
      default:
        return <ShieldIcon />;
    }
  };

  const getVerificationColor = (type: string) => {
    switch (type) {
      case 'EMAIL':
        return '#2196F3';
      case 'PHONE':
        return '#4CAF50';
      case 'ID':
        return '#9C27B0';
      case 'ADDRESS':
        return '#FF9800';
      default:
        return '#607D8B';
    }
  };

  const renderVerificationItem = (item: VerificationItem) => (
    <Card variant="outlined" sx={{ mb: 2 }} key={item.type}>
      <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            bgcolor: item.isVerified ? getVerificationColor(item.type) : '#e0e0e0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2,
          }}
        >
          {React.cloneElement(getVerificationIcon(item.type), {
            sx: { color: 'white' },
          })}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1">
            {item.type.replace('_', ' ')} Verification
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {item.isVerified
              ? `Verified on ${fmtDate(item.verifiedDate!)} via ${item.method}`
              : 'Not yet verified'}
          </Typography>
        </Box>
        <Box>
          {item.isVerified ? (
            <Chip label="Verified" color="success" icon={<CheckIcon />} />
          ) : item.canTrigger ? (
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleTriggerVerification(item.triggerAction!)}
              disabled={triggeringVerification === item.triggerAction}
            >
              {triggeringVerification === item.triggerAction ? (
                <CircularProgress size={20} />
              ) : (
                'Verify Now'
              )}
            </Button>
          ) : (
            <Chip label="Not Verified" color="default" icon={<CancelIcon />} />
          )}
        </Box>
      </CardContent>
    </Card>
  );

  const renderCredentialCard = (credential: TrustHubCredential) => (
    <Card variant="outlined" sx={{ mb: 2 }} key={credential.credentialId}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">{credential.credentialType}</Typography>
          <Chip
            label={credential.statusName}
            color={credential.status === 1 ? 'success' : 'warning'}
            size="small"
            sx={{ ml: 'auto' }}
          />
        </Box>
        <Typography variant="body2" color="text.secondary">
          Username: {credential.username}
        </Typography>
        {credential.businessName && (
          <Typography variant="body2" color="text.secondary">
            Business: {credential.businessName}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary">
          Created: {fmtDate(credential.created)}
        </Typography>
      </CardContent>
      <CardActions>
        <Chip
          label={credential.is2FAEnabled ? '2FA Enabled' : '2FA Disabled'}
          color={credential.is2FAEnabled ? 'success' : 'warning'}
          size="small"
        />
        <Chip
          label={credential.paymentComplete ? 'Payment Complete' : 'Payment Pending'}
          color={credential.paymentComplete ? 'success' : 'error'}
          size="small"
        />
      </CardActions>
    </Card>
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !dashboard) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Failed to load data'}</Alert>
      </Container>
    );
  }

  const { profile, verificationStatus, credentials, trustScore } = dashboard;
  const openCredentialingWizard = () => {
    window.location.assign(credentialingWizardUrl);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Trust Hub</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCredentialingWizard}
        >
          {credentials.length > 0 ? 'Add Credential' : 'Get Credentialed'}
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Trust Score
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ position: 'relative', display: 'inline-flex', mr: 2 }}>
                <CircularProgress
                  variant="determinate"
                  value={verificationStatus.trustScore}
                  size={80}
                  thickness={4}
                  color={
                    verificationStatus.trustScore >= 60
                      ? 'success'
                      : verificationStatus.trustScore >= 40
                        ? 'warning'
                        : 'error'
                  }
                />
                <Box
                  sx={{
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    position: 'absolute',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="h5" component="div">
                    {verificationStatus.trustScore}
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant="h6">{verificationStatus.trustScoreLevel}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Verification Level {verificationStatus.overallVerificationLevel}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>
              Quick Stats
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <ShieldIcon />
                </ListItemIcon>
                <ListItemText
                  primary={verificationStatus.verificationLevelName}
                  secondary="Current Level"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <BusinessIcon />
                </ListItemIcon>
                <ListItemText
                  primary={`${credentials.length} Credential${credentials.length !== 1 ? 's' : ''}`}
                  secondary="Active Credentials"
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
              <Tab label="Verifications" />
              <Tab label="Credentials" />
              <Tab label="Profile" />
            </Tabs>

            <TabPanel value={tabValue} index={0}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Complete verifications to increase your trust score and unlock additional features.
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Verification Progress
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={(verificationStatus.overallVerificationLevel / 5) * 100}
                  sx={{ height: 10, borderRadius: 5 }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {verificationStatus.overallVerificationLevel} of 5 verification levels completed
                </Typography>
              </Box>

              {renderVerificationItem(verificationStatus.email)}
              {renderVerificationItem(verificationStatus.phone)}
              {renderVerificationItem(verificationStatus.identity)}
              {renderVerificationItem(verificationStatus.address)}
              {verificationStatus.creditCard && renderVerificationItem(verificationStatus.creditCard)}
              {verificationStatus.bankAccount && renderVerificationItem(verificationStatus.bankAccount)}
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              {credentials.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <BusinessIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    No Credentials Yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Get credentialed to become a vendor, marketer, developer, or business on the Qixit platform.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={openCredentialingWizard}
                  >
                    Get Credentialed
                  </Button>
                </Box>
              ) : (
                <>
                  {credentials.map(renderCredentialCard)}
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={openCredentialingWizard}
                    sx={{ mt: 2 }}
                  >
                    Add Another Credential
                  </Button>
                </>
              )}
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              {profile && (
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Personal Information
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Username
                      </Typography>
                      <Typography>{profile.username}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Display Name
                      </Typography>
                      <Typography>{profile.displayName || '-'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Email
                      </Typography>
                      <Typography>{profile.email || '-'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Phone
                      </Typography>
                      <Typography>{profile.phoneNumber || '-'}</Typography>
                    </Grid>
                  </Grid>

                  {(profile.address1 || profile.businessName) && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle1" gutterBottom>
                        Business Information
                      </Typography>
                      <Grid container spacing={2}>
                        {profile.businessName && (
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Business Name
                            </Typography>
                            <Typography>{profile.businessName}</Typography>
                          </Grid>
                        )}
                        {profile.address1 && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                              Address
                            </Typography>
                            <Typography>
                              {profile.address1}
                              {profile.address2 && `, ${profile.address2}`}
                              <br />
                              {profile.city}, {profile.state} {profile.zip}
                              <br />
                              {profile.country}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </>
                  )}

                  <Box sx={{ mt: 3 }}>
                    <Button variant="outlined" onClick={() => navigate('/profile/settings')}>
                      Edit Profile
                    </Button>
                  </Box>
                </Box>
              )}
            </TabPanel>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default TrustHub;

